import { ErrorCode, ErrorResponse } from '@shared/types'
import { ExitEvent } from '@ui-common/state/types'
import { StartEvent } from '@ui-common/types'
import { debugLog, isErrorResponse } from '@ui-common/utils/helpers'
import { AnyActorRef, assign, DoneActorEvent, fromPromise, sendTo, setup, SpecialTargets } from 'xstate'

type RetryEvent = { type: 'RETRY' }
export type StartFetchEvent<TResponse> = StartEvent<{ callback: () => Promise<TResponse> | undefined }>
export type GetDataStepEvent = RetryEvent | ExitEvent
export type GetDataDoneEvent<TResponse> = { type: 'GET_DATA_DONE'; data: TResponse }

export const GET_DATA = 'getResponse'
const ERROR = 'error'
const INITIAL = 'initial'

type GetDataContext<TResponse> = {
  callback?: () => Promise<TResponse> | undefined
  error?: ErrorResponse<ErrorCode>
}

export type GetDataOutputCallback<TResponse> = {
  proceed: (data: TResponse) => void
}

export const getDataMachine = <TResponse>({
  parentService,
  callbacks,
}: {
  parentService?: AnyActorRef
  callbacks?: GetDataOutputCallback<TResponse>
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
}) => {
  // Parent can be defined either by passing it to function or invoking this machine as child machine
  const parent = parentService ?? SpecialTargets.Parent

  return setup({
    types: {
      context: {} as GetDataContext<TResponse>,
      events: {} as GetDataStepEvent | GetDataDoneEvent<TResponse> | StartFetchEvent<TResponse>,
      input: {} as GetDataContext<TResponse> | undefined,
    },
    actions: {
      proceedToNextStep: callbacks
        ? // Strange never-type issue in GET_DATA onDone handler if TResponse type used instead of any here
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (_, params: any) => callbacks.proceed(params)
        : sendTo(parent, (_, data) => ({ type: 'GET_DATA_DONE', data })),
      storeCallback: assign((_, params: () => Promise<TResponse> | undefined) => {
        return {
          callback: params,
        }
      }),
      storeError: assign((_, error) => {
        debugLog(error)
        return {
          error: isErrorResponse(error) ? error : undefined,
        }
      }),
    },
    actors: {
      getResponse: fromPromise(async ({ input }: { input: { callback: () => Promise<TResponse> | undefined } }) => {
        debugLog('Service: getResponse')
        return input.callback()
      }),
    },
  }).createMachine({
    id: 'getDataMachine',
    initial: INITIAL,
    context: ({ input }) => input,
    states: {
      [INITIAL]: {
        on: {
          START: {
            target: GET_DATA,
            actions: {
              type: 'storeCallback',
              params: ({ event }) => event.data.callback,
            },
          },
        },
      },
      [GET_DATA]: {
        invoke: {
          src: 'getResponse',
          input: ({ context, event }) => ({
            callback: event.type === 'START' ? event.data.callback : (context.callback as () => Promise<TResponse>),
          }),
          onDone: [
            {
              actions: {
                type: 'proceedToNextStep',
                params: ({ event }: { event: DoneActorEvent<TResponse> }) => event.output,
              },
            },
          ],
          onError: [
            {
              target: ERROR,
              actions: {
                type: 'storeError',
                params: ({ event }) => event.error,
              },
            },
          ],
        },
      },
      [ERROR]: {
        on: {
          RETRY: { target: GET_DATA },
        },
      },
    },
  })
}
